import {Async} from 'Core/models/Async';
import {EmptyString, NonEmptyString} from 'Core/models/EmptyString';
import {createAction} from 'Shared/utilities/actionCreator';
import {SSOAccount as SSOAccountDto, SSOResponse} from 'Users/api/apiTypes';
import {
  EmptySSOAccount,
  LoadingExistingSSOAccount,
  SavingSSOAccount,
  SSOAccount,
  SSOAccountWithError,
  ValidPassword,
  ValidSSOAccount,
  ValidUsername,
} from 'Users/models/SSOAccount';
import {SSOEntityId} from 'Users/models/SSOEntityId';

export enum SSOAccountActionType {
  CHANGE_IS_PRIMARY = 'ssoAccount/CHANGE_IS_PRIMARY',
  CHANGE_PASSWORD = 'ssoAccount/CHANGE_PASSWORD',
  CHANGE_USERNAME = 'ssoAccount/CHANGE_USERNAME',
  CLEAR = 'ssoAccount/CLEAR',
  DELETE = 'ssoAccount/DELETE',
  GET_ACCOUNT = 'ssoAccount/GET_ACCOUNT',
  LOAD_ACCOUNT = 'ssoAccount/LOAD_ACCOUNT',
  SAVE = 'ssoAccount/SAVE',
  SHOW_ERROR = 'ssoAccount/SHOW_ERROR',
  SSO = 'ssoAccount/SSO',
  CHANGE_SSO_URL = 'ssoAccount/CHANGE_SSO_URL',
}

export const clearAccount = () => createAction(SSOAccountActionType.CLEAR);
export const showError = (ssoAccount: ValidSSOAccount, errorMessage?: string) =>
  createAction(SSOAccountActionType.SHOW_ERROR, {ssoAccount, errorMessage});
export const getAccount = (encryptedIdentifier: string, userId: string, username: string) =>
  createAction(SSOAccountActionType.GET_ACCOUNT, {encryptedIdentifier, userId, username});
export const deleteAccount = (encryptedIdentifier: string, userId: string, username: string) =>
  createAction(SSOAccountActionType.DELETE, {encryptedIdentifier, userId, username});
export const loadAccount = (account: SSOAccountDto) => createAction(SSOAccountActionType.LOAD_ACCOUNT, {account});
export const sso = (encryptedIdentifier: string, userId: string, username: string, ssoEntityId: SSOEntityId) =>
  createAction(SSOAccountActionType.SSO, {encryptedIdentifier, userId, username, ssoEntityId});
export const changeUsername = (username: string) => createAction(SSOAccountActionType.CHANGE_USERNAME, {username});
export const changePassword = (password: string) => createAction(SSOAccountActionType.CHANGE_PASSWORD, {password});
export const changeIsPrimary = (isPrimary: boolean) =>
  createAction(SSOAccountActionType.CHANGE_IS_PRIMARY, {isPrimary});
export const saveAccount = (
  encryptedIdentifier: string,
  userId: string,
  account: ValidSSOAccount,
  isExisting: boolean
) => createAction(SSOAccountActionType.SAVE, {encryptedIdentifier, userId, account, isExisting});
export const changeSSOUrl = (ssoUrl: Async<SSOResponse>) => createAction(SSOAccountActionType.CHANGE_SSO_URL, {ssoUrl});

type SSOAccountState = SSOAccount;

const DEFAULT_STATE: SSOAccountState = EmptySSOAccount;

type SingleSignOnAction =
  | ReturnType<typeof loadAccount>
  | ReturnType<typeof getAccount>
  | ReturnType<typeof changeUsername>
  | ReturnType<typeof clearAccount>
  | ReturnType<typeof changePassword>
  | ReturnType<typeof showError>
  | ReturnType<typeof saveAccount>
  | ReturnType<typeof changeIsPrimary>
  | ReturnType<typeof changeSSOUrl>;

export default (state: SSOAccountState = DEFAULT_STATE, action: SingleSignOnAction): SSOAccountState => {
  switch (action.type) {
    case SSOAccountActionType.CLEAR:
      return DEFAULT_STATE;
    case SSOAccountActionType.SHOW_ERROR:
      return SSOAccountWithError.create(action.payload.ssoAccount, action.payload.errorMessage);
    case SSOAccountActionType.GET_ACCOUNT:
      return new LoadingExistingSSOAccount();
    case SSOAccountActionType.LOAD_ACCOUNT:
      return ValidSSOAccount.create(
        ValidUsername.create(NonEmptyString.create(action.payload.account.username!)),
        ValidPassword.create(new EmptyString()),
        action.payload.account.isPrimary!
      );
    case SSOAccountActionType.CHANGE_USERNAME:
      return ValidSSOAccount.create(
        ValidUsername.create(NonEmptyString.create(action.payload.username)),
        state.password,
        state.isPrimary
      );
    case SSOAccountActionType.CHANGE_PASSWORD:
      return ValidSSOAccount.create(
        state.username,
        ValidPassword.create(NonEmptyString.create(action.payload.password)),
        state.isPrimary
      );
    case SSOAccountActionType.CHANGE_IS_PRIMARY:
      return ValidSSOAccount.create(state.username, state.password, action.payload.isPrimary);
    case SSOAccountActionType.SAVE:
      return SavingSSOAccount.create(action.payload.account);
    case SSOAccountActionType.CHANGE_SSO_URL:
      return {
        ...state,
        ssoUrl: action.payload.ssoUrl,
      };
    default:
      return state;
  }
};
